import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, RouteReuseStrategy, withExperimentalAutoCleanupInjectors } from '@angular/router';
import { DEFAULTSCREEN, httpRequestsAuthInterceptor, USERTYPE } from '@pinnakl/auth/providers';
import { AuthPopupService, AuthSecurityStorageService } from '@pinnakl/authentication';
import { fileUrl } from '@pinnakl/core/data-providers';
import { providePinnaklEnvironment } from '@pinnakl/core/environment';
import { PinnaklErrorHandler } from '@pinnakl/core/errors-processing';
import { WebServiceProvider } from '@pinnakl/core/web-services';
import { AccountService } from '@pinnakl/poems';
import { AG_GRID_KEY, AG_GRID_KEY_TOKEN, HTTP_SERVICE_URL, LOCATION } from '@pinnakl/shared/constants';
import { UserTypes } from '@pinnakl/shared/types';
import { providePinnaklPrimeNg, UiToastMessageService } from '@pinnakl/shared/ui/prime';
import { GrowthBookService } from '@pinnakl/shared/util-external-services';
import { getAppInstanceToken, PageSubscriptionsHandler } from '@pinnakl/shared/util-helpers';
import { OptInRouteInjectorCleanupStrategy, PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';
import { AbstractSecurityStorage, provideAuth } from 'angular-auth-oidc-client';
import { provideHighcharts } from 'highcharts-angular';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { platformAppRoutes } from './app/app-routes';
import { AppComponent } from './app/app.component';
import { environment } from './environments';

const authConfig = {
  ...environment.authConfig,
  configId: getAppInstanceToken(environment.authConfig?.configId, location?.host, environment.appName),
};
if (environment.production) {
  enableProdMode();
}

fileUrl.url = environment.fileServiceUrl;

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    providePinnaklEnvironment(environment),
    provideAppInitializer(() => {
      inject(GrowthBookService).initialize();
    }),
    provideHttpClient(withXhr(), withInterceptors([httpRequestsAuthInterceptor])),
    WebServiceProvider,
    provideAuth({
      config: authConfig,
    }),
    providePinnaklPrimeNg(),
    {
      provide: LOCATION,
      useValue: location,
    },
    {
      provide: ErrorHandler,
      useClass: PinnaklErrorHandler,
    },
    { provide: AbstractSecurityStorage, useClass: AuthSecurityStorageService },
    { provide: USERTYPE, useValue: UserTypes.INTERNAL },
    { provide: DEFAULTSCREEN, useValue: { prod: 'dashboard', dev: 'dashboard' } },
    {
      provide: PinnaklUIToastMessage,
      useClass: UiToastMessageService,
    },
    {
      provide: AG_GRID_KEY_TOKEN,
      useValue: AG_GRID_KEY,
    },
    {
      provide: HTTP_SERVICE_URL,
      useValue: environment.tradingServiceUrl,
    },
    DialogService,
    MessageService,
    ConfirmationService,
    AccountService,
    provideHighcharts({ instance: () => import('@pinnakl/shared/util-highcharts').then(m => m.Highcharts) }),
    AuthPopupService,
    PageSubscriptionsHandler,
    { provide: RouteReuseStrategy, useClass: OptInRouteInjectorCleanupStrategy },
    provideRouter(platformAppRoutes, withExperimentalAutoCleanupInjectors()),
    PercentPipe,
    DatePipe,
    DecimalPipe,
    CurrencyPipe,
  ],
}).catch(err => console.error(err));
